import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ApolloError } from '@apollo/client';
import {
  useWidgetTemplateCreateMutation,
  useWidgetTemplateUpdateMutation,
  useWidgetTemplateDestroyMutation,
  WidgetTemplateCreateInputObject,
  WidgetTemplateUpdateInputObject,
  WidgetTemplate,
} from 'graphql-common';
import toast from '@lib/components/Toast/Toast';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';

interface UseWidgetTemplateActionsProps {
  refetchTemplates: () => void;
  onDeleteSuccess?: () => void;
  onUpdateSuccess?: (updatedTemplate: WidgetTemplate) => void;
}

interface UseWidgetTemplateActionsResult {
  // Create modal state
  isCreateModalOpen: boolean;
  openCreateModal: () => void;
  closeCreateModal: () => void;

  // Delete confirmation state
  isDeleteModalOpen: boolean;
  templateToDelete: string | null;
  openDeleteModal: (templateId: string) => void;
  closeDeleteModal: () => void;

  // Actions
  handleCreate: (attributes: WidgetTemplateCreateInputObject) => Promise<void>;
  handleUpdate: (
    id: string,
    attributes: WidgetTemplateUpdateInputObject,
  ) => Promise<void>;
  handleDelete: (id: string) => void;
  confirmDelete: () => void;

  // Loading states
  createLoading: boolean;
  updateLoading: boolean;
  deleteLoading: boolean;

  // Errors
  createError: ApolloError | undefined;
  updateError: ApolloError | undefined;
  deleteError: ApolloError | undefined;
}

export default function useWidgetTemplateActions({
  refetchTemplates,
  onDeleteSuccess,
  onUpdateSuccess,
}: UseWidgetTemplateActionsProps): UseWidgetTemplateActionsResult {
  const { t } = useTranslation();

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<string | null>(null);

  // Mutations
  const [createMutation, { loading: createLoading, error: createError }] =
    useWidgetTemplateCreateMutation({
      onCompleted: () => {
        toast({ content: t('home.create-widget-template-success') });
      },
      onError: (error) => showGraphQLErrors(error, t),
    });

  const [updateMutation, { loading: updateLoading, error: updateError }] =
    useWidgetTemplateUpdateMutation({
      onCompleted: () => {
        toast({ content: t('home.update-widget-template-success') });
      },
      onError: (error) => showGraphQLErrors(error, t),
    });

  const [destroyMutation, { loading: deleteLoading, error: deleteError }] =
    useWidgetTemplateDestroyMutation({
      onError: (error) => showGraphQLErrors(error, t),
    });

  // Modal handlers
  const openCreateModal = useCallback(() => {
    setIsCreateModalOpen(true);
  }, []);

  const closeCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
  }, []);

  const openDeleteModal = useCallback((templateId: string) => {
    setTemplateToDelete(templateId);
    setIsDeleteModalOpen(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setTemplateToDelete(null);
    setIsDeleteModalOpen(false);
  }, []);

  // Action handlers
  const handleCreate = useCallback(
    async (attributes: WidgetTemplateCreateInputObject) => {
      try {
        await createMutation({
          variables: { attributes },
        });
        closeCreateModal();
        refetchTemplates();
      } catch {
        // Empty catch: GraphQL/network errors are handled by onError callback (line 71)
        // This catch block handles any JS runtime errors, which shouldn't need UI feedback
      }
    },
    [createMutation, closeCreateModal, refetchTemplates],
  );

  const handleUpdate = useCallback(
    async (id: string, attributes: WidgetTemplateUpdateInputObject) => {
      try {
        const result = await updateMutation({
          variables: { id, attributes },
        });
        const updatedTemplate = result.data?.data;
        if (updatedTemplate) {
          onUpdateSuccess?.(updatedTemplate as WidgetTemplate);
        }
        refetchTemplates();
      } catch {
        // Empty catch: GraphQL/network errors are handled by onError callback (line 79)
        // This catch block handles any JS runtime errors, which shouldn't need UI feedback
      }
    },
    [updateMutation, onUpdateSuccess, refetchTemplates],
  );

  const handleDelete = useCallback(
    (id: string) => {
      toast({
        content: t('home.delete-widget-template-success'),
        closeCallback: () => {
          destroyMutation({
            variables: { id },
          })
            .then(() => {
              refetchTemplates();
              onDeleteSuccess?.();
            })
            .catch(() => {
              // GraphQL/network errors are handled by onError callback (line 84)
              // This catch prevents unhandled promise rejection for any other errors
            });
        },
      });
    },
    [t, destroyMutation, refetchTemplates, onDeleteSuccess],
  );

  const confirmDelete = useCallback(() => {
    if (templateToDelete) {
      closeDeleteModal();
      handleDelete(templateToDelete);
    }
  }, [templateToDelete, closeDeleteModal, handleDelete]);

  return {
    // Create modal state
    isCreateModalOpen,
    openCreateModal,
    closeCreateModal,

    // Delete confirmation state
    isDeleteModalOpen,
    templateToDelete,
    openDeleteModal,
    closeDeleteModal,

    // Actions
    handleCreate,
    handleUpdate,
    handleDelete,
    confirmDelete,

    // Loading states
    createLoading,
    updateLoading,
    deleteLoading,

    // Errors
    createError,
    updateError,
    deleteError,
  };
}
